/**
 * WidgetEditor — the widget STUDIO: a read-only VERSION VIEWER + PUBLISH surface.
 *
 * Left column:   file list (from the latest committed manifest) + the VERSIONS
 *                list. Double-click a version to select it.
 * Center column: READ-ONLY view of the selected version's files (Render / Raw).
 *                Authoring is gone — all editing moves to /widget-drive.
 * Right column:  live phone preview of the SELECTED version + a single
 *                Publish-to-Public / Delete-Public action.
 *
 * "Scratch" and the Private/Public preview tabs were retired: the PUBLIC pin
 * (widget.tags.release) is now the only pin. Publishing pins the EXACT selected
 * version to Public; Delete Public clears that pin. File bytes for the preview
 * and the read-only pane load by version label
 * (/api/widgets/{id}/files/{path}?version=<label>).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { widgetApi } from '../../services/widgetApi';
import { api } from '../../services/api';
import PhonePreview from './PhonePreview';
import WidgetRuntime from './WidgetRuntime';
import WidgetMainSessionPanel from './WidgetMainSessionPanel';
import MarkdownRenderer from '../MarkdownRenderer';
import { DeleteWidgetModal } from './WidgetCatalog';
import { buildDefaultLandingHtml, EXAMPLE_LANDING } from './landingDefault';
import { buildDefaultCoverHtml, COVER_PRESETS, normalizeCoverPreset, pickCoverPreset } from './coverDefault';
import { TAG_LABELS } from './tagLabels';
// The VERSIONS list lives in its own module (./versionPane) so the ViewIR
// studio operates the SAME real version ladder + public pin as this editor.
import { VersionPane, VersionBadge } from './versionPane';

// Light palette — mirrors PlaygroundView.jsx `V` tokens. Functional
// semantic colors (warn/release/danger) tuned for readability on white.
// Round-3: switched from the previous dark `#0F1115` background.
const COLORS = {
  bg: '#f5f5f7',
  bgPanel: '#ffffff',
  bgInput: '#f5f5f7',
  border: '#d8d8dc',
  borderActive: '#c7c7cc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  accent: '#5856d6',
  accentDim: '#eeedf5',
  warn: '#ff9500',
  release: '#34c759',
  dev: '#6e6e73',
  danger: '#b3261e',
  dangerBg: '#fff1f0',
  dangerBorder: '#ffd0cc',
};

// The widget id in the studio header. Ids are human slugs (v122), so render it
// in FULL (never sliced/wrapped) and click-copy the whole id to the clipboard.
function CopyableWidgetId({ id }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard unavailable — noop */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied!' : 'Click to copy widget id'}
      style={{
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 11, whiteSpace: 'nowrap',
        color: copied ? COLORS.accent : COLORS.textDim,
        fontFamily: 'ui-monospace, monospace',
      }}
    >
      {copied ? 'copied ✓' : id}
    </button>
  );
}

// Read-only file row — selecting a path opens it in the center pane's
// read-only viewer. No rename/delete affordance: authoring moved to
// /widget-drive.
function FileRow({ path, selected, onSelect }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', borderRadius: 6, background: selected ? COLORS.borderActive : 'transparent' }}
    >
      <button
        onClick={() => onSelect(path)}
        style={{
          flex: 1, minWidth: 0, background: 'transparent',
          color: selected ? COLORS.text : COLORS.textDim,
          border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12,
          textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        }}
      >
        {path}
      </button>
    </div>
  );
}

function FilePane({ paths, selected, onSelect }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px' }}>
        <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Files</span>
      </div>
      {paths.length === 0 && (
        <div style={{ padding: 12, color: COLORS.textDim, fontSize: 11 }}>No files yet</div>
      )}
      {paths.map(p => (
        <FileRow
          key={p}
          path={p}
          selected={selected === p}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// TagDot / VersionBadge / VersionRow / VersionPane were LIFTED to
// ./versionPane.jsx — the ViewIR studio renders the same real VERSIONS
// list + public pin, so there is one implementation, not two.

// PhonePreview lives in its own module (./PhonePreview.jsx) — keeping it
// here as a sibling function caused intermittent
// `ReferenceError: PhonePreview is not defined` under Vite Fast Refresh
// (the parent JSX rendered while the inner symbol was momentarily out of
// scope mid-HMR). Module boundary fixes that. Do not re-inline.


// Schema popup — shows the widget's PER-WIDGET data schema (the v3 contract
// served by GET /api/widgets/{id}/data-schema?version=<label>): the schema
// version, a field table per kind, the system's always-logged core fields,
// plus a "Run check" action that calls POST
// /api/widgets/{id}/schema/check?version=<label> (a green check auto-stamps
// the bundle). Visual language mirrors DeleteWidgetModal (fixed dim overlay +
// white rounded card).
function SchemaPopup({ widgetId, version, onClose, onUpgraded }) {
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);     // GET data-schema response
  const [loadError, setLoadError] = useState(null);
  const [check, setCheck] = useState(null);       // null | {checking} | result | {error}

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await widgetApi.getWidgetDataSchema(widgetId, { version });
        if (alive) setSchema(r);
      } catch (e) {
        if (alive) setLoadError(e.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [widgetId, version]);

  const runCheck = useCallback(async () => {
    setCheck({ checking: true });
    try {
      const r = await widgetApi.checkWidgetSchema(widgetId, { version });
      setCheck(r);
      if (r.passed && r.upgraded && onUpgraded) onUpgraded();
    } catch (e) {
      setCheck({ error: e.message || String(e) });
    }
  }, [widgetId, version, onUpgraded]);

  const ds = schema?.data_schema || null;
  const kinds = ds?.kinds && typeof ds.kinds === 'object' ? Object.entries(ds.kinds) : [];
  const requiredCore = Array.isArray(schema?.required_core) ? schema.required_core : [];
  const severityColor = (sev) =>
    sev === 'error' ? COLORS.danger : (sev === 'warning' ? COLORS.warn : COLORS.textDim);

  const cellHead = {
    textAlign: 'left', padding: '5px 8px', fontSize: 10, fontWeight: 700,
    color: COLORS.textDim, textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap',
  };
  const cell = {
    textAlign: 'left', padding: '5px 8px', fontSize: 11.5, color: COLORS.text,
    borderBottom: `1px solid ${COLORS.bgInput}`, verticalAlign: 'top',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.bgPanel, borderRadius: 12, padding: 20,
          width: 640, maxWidth: '94vw', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column', gap: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.18)', color: COLORS.text,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Data Schema</div>
          <span style={{
            fontSize: 10, fontWeight: 600, color: COLORS.dev,
            border: `1px solid ${COLORS.border}`, borderRadius: 4,
            padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>{version || '—'}{schema?.data_schema_version != null ? ` · v${schema.data_schema_version}` : ''}</span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.textDim }}
          >×</button>
        </div>

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, minHeight: 60 }}>
          {loading && <div style={{ fontSize: 12, color: COLORS.textDim, padding: '14px 2px' }}>Loading schema…</div>}
          {loadError && (
            <div style={{ fontSize: 12, color: COLORS.danger, background: COLORS.dangerBg, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: 8, padding: 10 }}>
              {loadError}
            </div>
          )}

          {!loading && !loadError && !ds && (
            <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.6, background: COLORS.bgInput, borderRadius: 8, padding: 12 }}>
              This widget predates per-widget schemas (v3). Author <code style={{ fontSize: 11 }}>data_schema</code> in
              {' '}<code style={{ fontSize: 11 }}>widget.json</code> via /widget-drive.
            </div>
          )}

          {!loading && ds && (
            <>
              <div style={{ fontSize: 11, color: COLORS.textDim }}>
                schema version <span style={{ fontFamily: 'ui-monospace, monospace', color: COLORS.text }}>{String(ds.version ?? '—')}</span>
              </div>
              {kinds.length === 0 && (
                <div style={{ fontSize: 12, color: COLORS.textDim }}>No kinds declared.</div>
              )}
              {kinds.map(([kindName, kindDef]) => {
                const fields = kindDef?.fields && typeof kindDef.fields === 'object' ? Object.entries(kindDef.fields) : [];
                return (
                  <div key={kindName} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      kind: <span style={{ fontFamily: 'ui-monospace, monospace', color: COLORS.accent }}>{kindName}</span>
                    </div>
                    <table style={{ borderCollapse: 'collapse', width: '100%', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                      <thead>
                        <tr>
                          <th style={cellHead}>name</th>
                          <th style={cellHead}>type</th>
                          <th style={cellHead}>required</th>
                          <th style={cellHead}>display</th>
                          <th style={cellHead}>description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.length === 0 && (
                          <tr><td colSpan={5} style={{ ...cell, color: COLORS.textDim }}>No fields.</td></tr>
                        )}
                        {fields.map(([name, f]) => (
                          <tr key={name}>
                            <td style={{ ...cell, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{name}</td>
                            <td style={{ ...cell, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap', color: COLORS.textDim }}>{String(f?.type ?? '—')}</td>
                            <td style={{ ...cell, whiteSpace: 'nowrap' }}>{f?.required ? '✓' : '—'}</td>
                            <td style={cell}>{String(f?.display ?? '—')}</td>
                            <td style={{ ...cell, color: COLORS.textDim }}>{String(f?.description ?? '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          )}

          {!loading && requiredCore.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                Core fields{' '}
                <span style={{
                  fontSize: 9, fontWeight: 700, color: COLORS.release,
                  background: COLORS.release + '1a', border: `1px solid ${COLORS.release}55`,
                  borderRadius: 999, padding: '2px 7px', marginLeft: 4,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>always logged by the system</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {requiredCore.map((name) => (
                  <span key={String(name)} style={{
                    fontSize: 11, fontFamily: 'ui-monospace, monospace',
                    background: COLORS.bgInput, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, padding: '2px 8px', color: COLORS.text,
                  }}>{String(name)}</span>
                ))}
              </div>
            </div>
          )}

          {/* "Run check" result — passed banner or severity-colored issues. */}
          {check && !check.checking && !check.error && (
            check.passed ? (
              <div style={{
                fontSize: 12, fontWeight: 600, color: COLORS.release,
                background: COLORS.release + '14', border: `1px solid ${COLORS.release}55`,
                borderRadius: 8, padding: 10,
              }}>
                ✓ Check passed{check.upgraded ? ` — bundle stamped at v${check.version}` : ''}.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger }}>
                  ✗ Check failed ({check.error_count ?? (check.issues || []).filter(i => i.severity === 'error').length} error{(check.error_count ?? 0) === 1 ? '' : 's'})
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, lineHeight: 1.5 }}>
                  {(check.issues || []).map((i, idx) => (
                    <li key={idx} style={{ color: severityColor(i.severity) }}>
                      <code style={{ fontSize: 10 }}>{i.path}</code> — {i.message}
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
          {check?.error && (
            <div style={{ fontSize: 12, color: COLORS.danger }}>check failed: {check.error}</div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={runCheck}
            disabled={!!check?.checking}
            title="Validate this version against the schema contract (green auto-stamps it)."
            style={{
              background: COLORS.accent, color: '#ffffff', border: 'none',
              borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600,
              cursor: check?.checking ? 'wait' : 'pointer', opacity: check?.checking ? 0.6 : 1,
            }}
          >
            {check?.checking ? 'Checking…' : 'Run check'}
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', color: COLORS.text, border: `1px solid ${COLORS.border}`,
              borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WidgetEditor() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [widget, setWidget] = useState(null);
  // The widget's main session view shown IN PLACE (overlay) when the preview's
  // "Chat with widget" FAB fires — instead of navigating to /playground/chat.
  // {sessionId, ask, widgetId, instanceId, tag} | null.
  const [mainSession, setMainSession] = useState(null);
  // Preview data source — derived from the URL so deep links work:
  //   ?data=user      → User Data (the absorbed Widget Use runtime: the
  //                     signed-in user's real instance feed + full app bridge)
  //   absent / other  → Mock Data (example_data.json; mutations gated)
  // ?instance=<id> picks which instance (templates) the User Data mode binds.
  const dataMode = useMemo(
    () => (new URLSearchParams(location.search).get('data') === 'user' ? 'user' : 'mock'),
    [location.search],
  );
  const requestedInstanceId = useMemo(
    () => new URLSearchParams(location.search).get('instance'),
    [location.search],
  );
  const setDataMode = useCallback((mode) => {
    const params = new URLSearchParams(location.search);
    if (mode === 'user') params.set('data', 'user');
    else { params.delete('data'); params.delete('instance'); }
    const q = params.toString();
    navigate(q ? `?${q}` : location.pathname, { replace: true });
  }, [navigate, location.search, location.pathname]);
  const [files, setFiles] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [fileContent, setFileContent] = useState('');
  // Middle (source) column fold — like the playground left column: folded on
  // start, a file click opens it to 3/4 of the preview pane's width, a click
  // anywhere outside (preview, header…) folds it back.
  const [editorOpen, setEditorOpen] = useState(false);
  // Middle column view: 'render' (md/html/svg/img/json displayed rendered)
  // or 'raw' (the plain source). READ-ONLY — authoring is via /widget-drive.
  const [editorView, setEditorView] = useState('render');
  // Blob URL for image files in Render view (binary — can't ride the textarea).
  const [renderImageUrl, setRenderImageUrl] = useState(null);
  const leftColRef = useRef(null);
  const centerColRef = useRef(null);
  const [previewSha, setPreviewSha] = useState('');
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  // The version_label the studio is VIEWING. Everything (preview, read-only
  // file pane, schema popup) reads THIS exact committed version. Defaulted on
  // open to the PUBLIC pin (widget.tags.release) if set, else the newest label
  // in versionList (see the default-select effect below).
  const [selectedVersion, setSelectedVersion] = useState(null);
  // The merged VERSIONS list (v96): local versions + the cross-env shared
  // list, each entry carrying THIS env's PUBLIC badge. Refetched whenever the
  // widget summary reloads (publish / clear-public reload).
  const [versionList, setVersionList] = useState(null);
  // null versionList is ambiguous (loading vs fetch-error vs empty); this flag
  // disambiguates so the pane never falsely reads "No versions yet" mid-load.
  const [versionsError, setVersionsError] = useState(false);
  // Theme the preview injects via __USER_DATA__.appearance so the designer can
  // verify light/dark/auto without leaving Studio. 'auto' follows the OS.
  const [previewAppearance, setPreviewAppearance] = useState('auto');
  // What the phone preview shows — a 4-way dropdown next to Light/Dark/Auto so
  // the designer can iterate on all four faces of a widget without leaving
  // Studio: 'widget' (live index.html) · 'cover' (catalog tile cover.html) ·
  // 'icon' (the widget mark) · 'landing' (the intro page — landing.html, or the
  // host default page rendering landing.json). Derived booleans below keep the
  // per-face effects/render readable.
  const [previewMode, setPreviewMode] = useState('widget');
  const previewCover = previewMode === 'cover';
  const previewIcon = previewMode === 'icon';
  const previewLanding = previewMode === 'landing';
  // Which of the 5 cover presets the Cover preview renders — preview-only
  // (compare all 5 host presets). Defaults to the selected version's manifest
  // cover_preset, else a deterministic pick from the widget id.
  const [previewCoverPreset, setPreviewCoverPreset] = useState('count');
  // Landing TYPE the Landing preview shows: "json" (host default page) or
  // "html" (the widget's own landing.html) — preview-only. Defaults to the
  // selected version's committed landing_type.
  const [previewLandingType, setPreviewLandingType] = useState('json');
  // Editor header overflow menu (Config — bundle download + danger zone).
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  // Schema popup — shows the selected version's per-widget data schema (GET
  // /api/widgets/{id}/data-schema?version=<label>) and hosts "Run check".
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const headerMenuRef = useRef(null);
  const previewRef = useRef(null);

  // The PUBLIC pin's version_label (widget.tags.release), or null.
  const releaseLabel = widget?.tags?.release?.version_label || null;
  // Whether the version being viewed IS the one pinned to Public.
  const isReleaseSelected = !!selectedVersion && selectedVersion === releaseLabel;

  const reloadWidget = useCallback(async () => {
    setError(null);
    try {
      // /api/widgets returns my list w/ tag pins. Filter to this widget for fresh tag state.
      const [mine, snap] = await Promise.all([
        widgetApi.listMine().catch(() => ({ widgets: [] })),
        widgetApi.getWidget(widgetId).catch(() => null),
      ]);
      const fromList = (mine.widgets || []).find(w => w.id === widgetId);
      if (fromList) {
        setWidget(fromList);
      } else if (snap) {
        // Not in "my widgets" — e.g. an admin editing a PUBLIC widget owned by
        // someone else. GET /api/widgets/{id} (snap) carries the full tag pins
        // + bundle manifests, so use them directly. (Previously wiped to
        // tags:{}, which blanked file content / preview / the Private·Public
        // toggle for any widget the user didn't own.)
        setWidget(snap);
      } else {
        setWidget(null);
      }
      // Refresh file tree from bundle manifest of the dev tag. Use snap when the
      // widget isn't in the caller's own list (admin editing a public widget).
      const devManifest = (fromList || snap)?.tags?.dev?.bundle_manifest;
      if (devManifest?.files?.length) {
        setFiles(devManifest.files.map(f => f.path).sort());
      } else {
        // No committed bundle yet — show the seed files we'd populate.
        // example_data.json is the new (v07) data slot consumed by the
        // preview iframe via window.__WIDGET_DATA__; we surface it in
        // the file pane so the author can edit the fixture directly. The
        // Studio create-widget flow seeds it with `{}` on first commit.
        setFiles(['widget.json', 'index.html', 'SKILL.md', 'example_data.json']);
      }
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [widgetId]);

  useEffect(() => { reloadWidget(); }, [reloadWidget]);

  // Default the SELECTED version on open: the PUBLIC pin (release) when it
  // exists, else the NEWEST committed label (versionList is ordered newest-
  // first, so [0] is the max). One-shot per widget — a manual version click
  // wins over it. Runs once both the widget summary and the versions list
  // have loaded (release may exist before the list arrives).
  const defaultedVersionRef = useRef(null);
  useEffect(() => {
    if (!widget || defaultedVersionRef.current === widgetId) return;
    const rel = widget.tags?.release?.version_label || null;
    const newest = versionList?.versions?.[0]?.version_label || null;
    const pick = rel || newest;
    if (!pick) return; // nothing committed yet, or versions still loading
    defaultedVersionRef.current = widgetId;
    setSelectedVersion(pick);
  }, [widget, widgetId, versionList]);

  // Scroll the playground main column to top whenever we mount or switch
  // widgets. Growth flagged that returning to /playground/widget/{id} from
  // the catalog inherited the previous page's scrollTop (≈351px) and the
  // editor header / toolbar was clipped above the fold. The selector is
  // scoped to the playground layout so other routes aren't touched.
  useEffect(() => {
    const main = document.querySelector('.pg-layout-main');
    if (main && typeof main.scrollTo === 'function') {
      main.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [widgetId]);

  // The content window REPLACES the preview while open; it closes only via
  // the explicit "✕" on its bar (the old click-outside fold surprised users
  // when the panes sat side-by-side).

  // File-type classification for the source column's Render view.
  const fileExt = (selectedPath || '').split('.').pop().toLowerCase();
  const isImageFile = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'ico'].includes(fileExt);
  const isRenderable = isImageFile || ['md', 'markdown', 'html', 'htm', 'svg', 'json'].includes(fileExt);

  // Default view per file: renderable types open rendered, code opens raw.
  // Images are render-only (binary bytes don't survive the textarea).
  useEffect(() => {
    const ext = (selectedPath || '').split('.').pop().toLowerCase();
    const renderable = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'ico', 'md', 'markdown', 'html', 'htm', 'svg', 'json'].includes(ext);
    setEditorView(renderable ? 'render' : 'raw');
  }, [selectedPath]);

  // Image files in Render view — fetch the SELECTED version's bytes as a blob
  // and show them via an object URL.
  useEffect(() => {
    if (!selectedPath || !isImageFile || editorView !== 'render' || !selectedVersion) {
      setRenderImageUrl((prev) => { if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000); return null; });
      return undefined;
    }
    let alive = true;
    (async () => {
      let blob = null;
      try { blob = await widgetApi.readFileAsBlob(widgetId, selectedPath, { version: selectedVersion }); } catch { /* placeholder below */ }
      if (!alive) return;
      const url = blob ? URL.createObjectURL(blob) : null;
      setRenderImageUrl((prev) => { if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000); return url; });
    })();
    return () => { alive = false; };
  }, [widgetId, selectedPath, isImageFile, editorView, selectedVersion, previewSha]);

  // Default-select index.html (then widget.json, then first alphabetical)
  // once the list is known. SKILL.md sorts before index.html and is the
  // first item under a naive sort — Growth pasted 10KB of HTML into the
  // wrong file before noticing — so we always prefer index.html when it
  // exists. widget.json is the next-most-useful default since editing the
  // manifest is the path for renaming / adding files.
  useEffect(() => {
    if (selectedPath || !files.length) return;
    const sorted = [...files].sort();
    if (files.includes('index.html')) setSelectedPath('index.html');
    else if (files.includes('widget.json')) setSelectedPath('widget.json');
    else setSelectedPath(sorted[0]);
  }, [files, selectedPath]);

  // Load the SELECTED version's file content into the READ-ONLY viewer. Reads
  // the exact committed version's bytes (readFileAsBlob({version}).text()).
  // Image files are handled by the render effect above (their bytes ride a
  // blob URL, not this text state), so skip them here.
  useEffect(() => {
    if (!selectedPath || !selectedVersion || isImageFile) {
      setFileContent('');
      return;
    }
    let alive = true;
    (async () => {
      try {
        const blob = await widgetApi.readFileAsBlob(widgetId, selectedPath, { version: selectedVersion });
        const text = await blob.text();
        if (alive) setFileContent(text);
      } catch (err) {
        if (alive) setFileContent(`/* failed to load ${selectedPath} @ ${selectedVersion}: ${err.message} */`);
      }
    })();
    return () => { alive = false; };
  }, [widgetId, selectedPath, selectedVersion, isImageFile, previewSha]);

  // SSE listener: a widget_file_updated event for this widget cache-busts the
  // preview (e.g. a /widget-drive commit landed while the studio was open).
  // The studio is read-only now, so this only bumps previewSha — no per-file
  // dirty tracking. `withCredentials: true` forwards the HttpOnly session
  // cookie so browser EventSource auth succeeds.
  useEffect(() => {
    if (!widget) return;
    const viUserId = api.getViUserId();
    if (!viUserId) return;
    const es = new EventSource('/api/users/events', { withCredentials: true });
    const handleFileUpdated = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.widget_id !== widget.id) return;
        setPreviewSha(msg.sha256?.slice(0, 12) || String(Date.now()));
      } catch { /* parse error — skip */ }
    };
    es.addEventListener('widget_file_updated', handleFileUpdated);
    return () => es.close();
  }, [widget]);

  // Blob-URL preview source. The iframe inside PhonePreview cannot carry
  // the Firebase Authorization header, so feeding it the raw auth-gated
  // /api/widgets/{id}/files/index.html URL always 401s. Instead we
  // authFetch the bytes ourselves (with auth), wrap them in a Blob with
  // the server's Content-Type, and hand the iframe a blob: URL that
  // requires no auth to read. We refresh on every previewSha change
  // (widget_file_updated SSE) and revoke the previous object URL so memory
  // doesn't grow unbounded as the preview refreshes.
  const [previewUrl, setPreviewUrl] = useState(null);
  // Icon preview (Icon face only). Mirrors the cover blob-fetch (the iframe/img
  // can't carry the auth header), reading the manifest-declared icon file (from
  // the selected version, then a committed tag) and falling back to the public
  // default SVG when the widget ships no icon yet.
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  // Best available manifest for the manifest-derived preview config
  // (cover_type / cover_config / icon / landing_type). Only the dev & release
  // bundle manifests are available client-side (not per-version ones), so we
  // prefer the PUBLIC pin's manifest when it's the selected version, else the
  // dev manifest, then whichever exists. Preview-only — no per-version edits.
  const previewManifest = (isReleaseSelected
    ? widget?.tags?.release?.bundle_manifest
    : widget?.tags?.dev?.bundle_manifest)
    || widget?.tags?.release?.bundle_manifest
    || widget?.tags?.dev?.bundle_manifest
    || null;
  // Default the Cover preset switch to the manifest cover_preset, else a
  // deterministic pick from the widget id. Preview-only comparison of presets.
  const manifestCoverPreset = previewManifest?.cover_preset || null;
  useEffect(() => {
    setPreviewCoverPreset(manifestCoverPreset ? normalizeCoverPreset(manifestCoverPreset) : pickCoverPreset(widgetId));
  }, [manifestCoverPreset, widgetId, selectedVersion]);
  // Landing type follows the manifest's committed landing_type (default "json"
  // = host default page). Preview-only.
  const manifestLandingType = previewManifest?.landing_type || null;
  useEffect(() => {
    setPreviewLandingType(manifestLandingType || 'json');
  }, [manifestLandingType, widgetId, selectedVersion]);
  useEffect(() => {
    let alive = true;
    let createdUrl = null;
    (async () => {
      const fallbackHtml = `<!doctype html><html><body style="font-family:-apple-system,system-ui,sans-serif;color:#6e6e73;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;padding:24px;text-align:center;background:#f5f5f7;">No preview available for this version.</body></html>`;
      const placeholderBlob = () => new Blob([fallbackHtml], { type: 'text/html; charset=utf-8' });

      let blob = null;
      // Last-ditch fallback tag when the selected version doesn't carry the
      // requested entry file (e.g. a version with no cover.html). Order:
      // dev → release.
      const fallbackTag = ['dev', 'release'].find(t => widget?.tags?.[t]?.version_label) || null;

      // The Widget face is rendered by WidgetRuntime (User/Mock data modes)
      // — this pipeline only feeds the Cover / Icon / Landing previews.
      if (!previewCover && !previewIcon && !previewLanding) {
        setPreviewUrl(prev => { if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000); return null; });
        return;
      }

      // Icon mode shows the widget mark via PhonePreview (iconUrl), not the
      // iframe — clear the preview blob and let the dedicated icon effect drive.
      if (previewIcon) {
        setPreviewUrl(prev => { if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000); return null; });
        return;
      }

      // Landing form follows the Landing-type selector (`previewLandingType`),
      // seeded from the selected version's committed landing_type:
      //   "json" (or unset) → render the HOST DEFAULT landing page (EXAMPLE_LANDING
      //     so the preview matches the "Landing Presets" gallery exactly).
      //   "html" → fall through and render the widget's own landing.html.
      if (previewLanding && previewLandingType !== 'html') {
        if (!alive) return;
        const html = buildDefaultLandingHtml(EXAMPLE_LANDING, { appearance: previewAppearance });
        createdUrl = URL.createObjectURL(new Blob([html], { type: 'text/html; charset=utf-8' }));
        setPreviewUrl(prev => { if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000); return createdUrl; });
        return;
      }

      // Cover: cover_type "preset" (the DEFAULT — absent → preset) is rendered
      // by the host from one of 5 presets, NOT a bundle file. Generate that HTML
      // here and skip the file fetch; the inject step below still feeds it
      // __WIDGET_DATA__/__USER_DATA__ (the preset reads them at runtime). Only
      // cover_type:"html" reads a hand-authored cover.html.
      if (previewCover) {
        const coverType = previewManifest?.cover_type || 'preset';
        if (coverType !== 'html') {
          const coverCfg = previewManifest?.cover_config || null;
          blob = new Blob([buildDefaultCoverHtml(previewCoverPreset, coverCfg)], { type: 'text/html; charset=utf-8' });
        }
      }
      const entryFile = previewCover ? 'cover.html' : (previewLanding ? 'landing.html' : 'index.html');
      // Read the entry file from the SELECTED version; fall back to whichever
      // committed tag carries it (a version may omit cover.html / landing.html).
      if (!blob && selectedVersion) {
        try {
          blob = await widgetApi.readFileAsBlob(widgetId, entryFile, { version: selectedVersion });
        } catch { /* try the tag fallback below */ }
      }
      if (!blob && fallbackTag) {
        try {
          blob = await widgetApi.readFileAsBlob(widgetId, entryFile, { tag: fallbackTag });
        } catch { /* fall through to placeholder */ }
      }
      if (!alive) return;

      // v07: inject window.__WIDGET_DATA__ from example_data.json BEFORE
      // the widget's own <script> runs. We read the JSON from the SELECTED
      // version (fallback to a committed tag) and splice an inline script tag
      // at the start of <head>. The blob: URL is sandboxed so we can't pass
      // auth headers for an external data fetch — inlining is the only path
      // that survives `sandbox="allow-scripts"`.
      let dataJson = '{}';
      try {
        if (selectedVersion) {
          const b = await widgetApi.readFileAsBlob(widgetId, 'example_data.json', { version: selectedVersion });
          dataJson = await b.text();
        } else if (fallbackTag) {
          dataJson = await widgetApi.readFile(widgetId, 'example_data.json', { tag: fallbackTag });
        }
      } catch { dataJson = '{}'; /* missing example_data.json is fine — pre-seed widgets */ }
      // Validate the read content is JSON before injecting; broken JSON
      // would crash the widget's script. Designer agents sometimes
      // include `//` line comments or trailing commas — strict JSON.parse
      // rejects those and the preview goes blank silently, which Growth
      // can't debug. Try one defensive fixup pass (strip line/block
      // comments + trailing commas) before giving up. If it still won't
      // parse, fall back to {}.
      let safeData = '{}';
      const tryParse = (s) => { try { JSON.parse(s); return s; } catch { return null; } };
      let raw = dataJson || '{}';
      let parsed = tryParse(raw);
      if (!parsed) {
        const stripped = raw
          .replace(/\/\*[\s\S]*?\*\//g, '')   // /* … */ block comments
          .replace(/(^|[^:])\/\/.*$/gm, '$1') // // line comments (skip http://)
          .replace(/,\s*(\]|\})/g, '$1');     // trailing commas
        parsed = tryParse(stripped);
      }
      safeData = parsed || '{}';
      if (blob) {
        try {
          const text = await blob.text();
          // Host-injected user metadata mirrors the Widget-face runtime
          // (WidgetRuntime) so a widget that reads `__USER_DATA__.name`
          // sees a non-null name in Studio preview too. Fall back to a
          // neutral demo label so the rendered preview never reads as
          // "Hi, null" while the developer is iterating.
          const userMeta = {
            vi_user_id: api.getViUserId?.() || null,
            name: api.getUserDisplayName?.() || 'Demo',
            appearance: previewAppearance,   // light | dark | auto — widget themes from this
            // Cover presets read __WIDGET_DATA__.title then fall back to U.title;
            // surface the widget's real name so the Cover preview isn't "Widget".
            title: widget?.title || null,
          };
          let userJson = '{}';
          try { userJson = JSON.stringify(userMeta); } catch { userJson = '{}'; }
          const inject = `<script>window.__WIDGET_DATA__=${safeData};window.__USER_DATA__=${userJson};</script>`;
          // Splice inside <head> if present, else prepend to the document.
          const headIdx = text.search(/<head[^>]*>/i);
          let merged;
          if (headIdx !== -1) {
            const after = text.indexOf('>', headIdx) + 1;
            merged = text.slice(0, after) + inject + text.slice(after);
          } else {
            merged = inject + text;
          }
          blob = new Blob([merged], { type: blob.type || 'text/html; charset=utf-8' });
        } catch { /* keep original blob on injection failure */ }
      }
      const finalBlob = blob || placeholderBlob();
      createdUrl = URL.createObjectURL(finalBlob);
      setPreviewUrl(prev => {
        // Revoke the previous blob URL so we don't leak object URL slots as
        // the preview refreshes (version / appearance / preset changes).
        if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
        return createdUrl;
      });
    })();
    return () => {
      alive = false;
      // Don't revoke createdUrl here — the iframe is still showing it.
      // The next-effect's setPreviewUrl(prev => …) handles revocation
      // of the previous URL right before the new one is published.
    };
  }, [widget, widgetId, previewSha, selectedVersion, previewAppearance, previewCover, previewCoverPreset, previewIcon, previewLanding, previewLandingType]);

  // Revoke any outstanding blob URL when the editor unmounts so we don't
  // leak the final URL across route changes.
  useEffect(() => {
    return () => {
      setPreviewUrl(prev => {
        if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
        return null;
      });
    };
  }, []);

  // Icon preview source (Cover view only). Built like the cover blob: the
  // <img> can't carry the auth header, so we authFetch the icon bytes and
  // hand it a blob: URL. Resolves the declared `widget.json.icon` filename
  // from the best available manifest, then tries icon.svg / icon.png; on a
  // total miss shows the public default SVG.
  useEffect(() => {
    // The icon blob feeds the Icon preview only (the Cover no longer shows it).
    if (!previewIcon) {
      setIconPreviewUrl(prev => {
        if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
        return null;
      });
      return undefined;
    }
    let alive = true;
    let createdUrl = null;
    (async () => {
      const fallbackTag = ['dev', 'release'].find(t => widget?.tags?.[t]?.version_label) || null;
      const declared = previewManifest?.icon || null;
      const candidates = [];
      if (declared) candidates.push(declared);
      for (const c of ['icon.svg', 'icon.png']) if (!candidates.includes(c)) candidates.push(c);

      let blob = null;
      for (const path of candidates) {
        if (selectedVersion) {
          try { blob = await widgetApi.readFileAsBlob(widgetId, path, { version: selectedVersion }); } catch { /* try next */ }
        }
        if (!blob && fallbackTag) {
          try { blob = await widgetApi.readFileAsBlob(widgetId, path, { tag: fallbackTag }); } catch { /* try next */ }
        }
        if (blob) break;
      }
      if (!alive) return;
      if (blob) {
        createdUrl = URL.createObjectURL(blob);
        setIconPreviewUrl(prev => {
          if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
          return createdUrl;
        });
      } else {
        setIconPreviewUrl(prev => {
          if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
          return '/default-widget-icon.svg';
        });
      }
    })();
    return () => { alive = false; };
  }, [widget, widgetId, previewSha, selectedVersion, previewIcon, previewManifest?.icon]);

  // Revoke the icon blob URL on unmount.
  useEffect(() => {
    return () => {
      setIconPreviewUrl(prev => {
        if (prev && prev.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* gone */ } }, 60_000);
        return null;
      });
    };
  }, []);

  // Keep the VERSIONS list in sync with the widget summary — `widget` is a
  // fresh object after every reloadWidget(), so publish / clear-public all
  // refetch the merged list (and its per-env public badge) automatically.
  useEffect(() => {
    if (!widgetId) return undefined;
    let alive = true;
    setVersionsError(false);
    widgetApi.listVersionLabels(widgetId)
      .then((r) => { if (alive) { setVersionList(r); setVersionsError(false); } })
      // Keep any previously-loaded list visible on a refetch failure; only flag
      // the error so the empty state reads as an error, not "no versions".
      .catch(() => { if (alive) setVersionsError(true); });
    return () => { alive = false; };
  }, [widgetId, widget]);

  // VERSIONS-list selection: pick a version to VIEW — the preview + read-only
  // file pane repaint to that exact version. Pure client state, no round-trip.
  const handleSelectVersion = useCallback((entry) => {
    if (entry?.version_label) setSelectedVersion(entry.version_label);
  }, []);

  // Delete Public — clear the release pin (server also flips is_public=false).
  const handleClearTag = useCallback(async (tag) => {
    setActionBusy(true); setError(null);
    try {
      await widgetApi.clearTag(widgetId, tag);
      await reloadWidget();
    } catch (e) {
      setError(`Delete ${tag === 'release' ? 'Public' : tag} failed: ${e.message || e}`);
    } finally {
      setActionBusy(false);
    }
  }, [widgetId, reloadWidget]);

  // Outside click → close the header overflow menu.
  useEffect(() => {
    if (!headerMenuOpen) return undefined;
    const onDoc = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [headerMenuOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await widgetApi.deleteWidget(widgetId);
      navigate('/playground/widget/studio');
    } catch (e) {
      setDeleteError(e.message || String(e));
    } finally {
      setDeleteBusy(false);
    }
  }, [widgetId, navigate]);

  // Publish the SELECTED version to the PUBLIC pin. Schema-gated: a 422 carries
  // detail.issues — surface the first few so the user knows what to fix.
  const handlePublish = useCallback(async () => {
    if (!selectedVersion) { setError('Select a version to publish first.'); return; }
    setActionBusy(true); setError(null);
    try {
      await widgetApi.publish(widgetId, selectedVersion);
      await reloadWidget();
    } catch (e) {
      let msg = e.message || String(e);
      try {
        const body = e.body ? JSON.parse(e.body) : null;
        const issues = body?.detail?.issues;
        if (Array.isArray(issues) && issues.length) {
          msg = `blocked: ${issues.slice(0, 3).map((i) => i.message || i.path || String(i)).join('; ')}`;
        }
      } catch { /* keep msg */ }
      setError(`Publish ${selectedVersion} failed: ${msg}`);
    } finally {
      setActionBusy(false);
    }
  }, [widgetId, selectedVersion, reloadWidget]);

  const handleDownloadZip = useCallback(async () => {
    setDownloadBusy(true); setError(null);
    try {
      // Prefer the highest-published tag the user actually has; fall back
      // to dev so a never-published widget still has something to download.
      const available = ['release', 'dev'].filter(t => widget?.tags?.[t]?.version_label);
      const candidates = available.length ? available : ['dev'];
      const slug = widget?.slug
        || widget?.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        || widgetId;
      let lastErr = null;
      for (const tag of candidates) {
        try {
          const label = widget?.tags?.[tag]?.version_label || tag;
          await widgetApi.downloadBundle(widgetId, { tag, filename: `${slug}-${label}.zip` });
          return;
        } catch (e) { lastErr = e; }
      }
      throw lastErr || new Error('no tagged bundle available');
    } catch (e) {
      setError(`Download ZIP failed: ${e.message || e}`);
    } finally {
      setDownloadBusy(false);
    }
  }, [widget, widgetId]);

  // Header "Refresh" — re-pull the whole page: widget summary + tags (which
  // also refetches the VERSIONS list), bust the preview blob cache, and
  // remount WidgetRuntime via the keyed nonce so its instance feed /
  // sessions / HTML reload too.
  const [runtimeNonce, setRuntimeNonce] = useState(0);
  const handleRefreshAll = useCallback(async () => {
    setActionBusy(true);
    try {
      await reloadWidget();
      setPreviewSha(String(Date.now()));
      setRuntimeNonce((n) => n + 1);
    } finally {
      setActionBusy(false);
    }
  }, [reloadWidget]);

  if (!widget) {
    return (
      <div style={{ height: '100%', background: COLORS.bg, color: COLORS.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
        {error ? <div style={{ color: COLORS.danger }}>{error}</div> : 'Loading widget…'}
      </div>
    );
  }

  const tags = widget.tags || {};

  // The read-only file list — the latest committed manifest's paths.
  const displayPaths = files;

  // Outer container fills .pg-layout-main exactly (height: 100%) so
  // the chat bar stays inside the viewport. Growth saw the chat input
  // render 300+ px below the fold (`scrollHeight > clientHeight`)
  // because the editor used `flex: 1` against a column with no
  // height constraint and an `overflow: hidden` ancestor. Locking
  // to 100% + min-height: 0 across the column chain lets each
  // pane scroll its own children instead of pushing the page.
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: COLORS.bg, color: COLORS.text, overflow: 'hidden' }}>
      <header style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 18px', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.bgPanel,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/playground/widget/studio')}
            style={{ background: 'transparent', color: COLORS.textDim, border: 'none', cursor: 'pointer', fontSize: 12 }}
          >
            ← Widgets
          </button>
          <h1 style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, margin: 0 }}>
            {widget.title || '(untitled widget)'}
          </h1>
          <CopyableWidgetId id={widgetId} />
          {(() => {
            // Read-only visibility badge — driven by `is_public` (the actual
            // catalog visibility), NOT by the mere existence of a release
            // version. Unpublish flips is_public=false but KEEPS the release
            // pointer (so old log cards still resolve), so a release version
            // can persist on a Private widget — keying off the tag mislabeled
            // those as Public while the catalog correctly listed them Private.
            const isPublic = !!widget?.is_public;
            const title = isPublic
              ? `Public — visible in the public catalog.`
              : `Private — not in the public catalog. Publish to ${TAG_LABELS.release} to make this widget public.`;
            const bg = isPublic ? '#e6f7eb' : '#f1f1f3';
            const fg = isPublic ? COLORS.release : COLORS.textDim;
            const border = isPublic ? '#bfe5c8' : COLORS.border;
            return (
              <span
                title={title}
                style={{
                  marginLeft: 4,
                  background: bg, color: fg, border: `1px solid ${border}`,
                  borderRadius: 999, padding: '3px 10px', fontSize: 11,
                  fontWeight: 600, lineHeight: 1.4,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <span style={{ fontSize: 12 }}>{isPublic ? '🌐' : '🔒'}</span>
                {isPublic ? 'Public' : 'Private'}
              </span>
            );
          })()}
          {/* Refresh — re-pull everything this page shows: widget summary +
              tags, file list, VERSIONS, the preview HTML, and (via a keyed
              remount) the runtime's instance feed/sessions. For when a
              /widget-drive commit or an agent log landed while the page
              was open. */}
          <button
            onClick={handleRefreshAll}
            disabled={actionBusy}
            title="Reload the widget: files, versions, preview, and user data."
            style={{
              marginLeft: 2,
              background: '#f1f1f3', color: COLORS.textDim,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 999, padding: '3px 10px', fontSize: 11,
              fontWeight: 600, lineHeight: 1.4,
              cursor: actionBusy ? 'not-allowed' : 'pointer',
              opacity: actionBusy ? 0.6 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <span style={{ fontSize: 12 }}>⟳</span>
            Refresh
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Two-state publish action. The selected version is EITHER the
              PUBLIC pin (→ Delete Public) or a candidate (→ Publish to Public,
              which pins that exact version). */}
          {isReleaseSelected ? (
            <button
              onClick={() => handleClearTag('release')}
              disabled={actionBusy}
              title="Clear the Public pin (also removes the widget from the public catalog)."
              style={{
                border: `1px solid ${COLORS.dangerBorder}`, borderRadius: 6,
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                background: 'transparent', color: COLORS.danger,
                cursor: actionBusy ? 'not-allowed' : 'pointer', opacity: actionBusy ? 0.5 : 1,
              }}
            >
              Delete Public
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={actionBusy || !selectedVersion}
              title={selectedVersion
                ? `Pin ${selectedVersion} to Public (makes the widget publicly visible).`
                : 'Select a version to publish.'}
              style={{
                border: 'none', borderRadius: 6,
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                background: COLORS.release, color: '#ffffff',
                cursor: (actionBusy || !selectedVersion) ? 'not-allowed' : 'pointer',
                opacity: (actionBusy || !selectedVersion) ? 0.5 : 1,
              }}
            >
              Publish to Public
            </button>
          )}
          <button
            onClick={() => setSchemaOpen(true)}
            title="View this widget's per-widget data schema and run a check"
            style={{
              background: COLORS.accentDim,
              color: COLORS.accent,
              border: `1px solid ${COLORS.accent}55`,
              borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Schema
          </button>
          <div ref={headerMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setHeaderMenuOpen(v => !v)}
              aria-label="Widget config"
              title="Widget config — bundle download + danger zone"
              style={{
                background: 'transparent', color: COLORS.textDim,
                border: `1px solid ${COLORS.border}`, borderRadius: 6,
                padding: '6px 10px', fontSize: 12, fontWeight: 600, lineHeight: '1',
                cursor: 'pointer',
              }}
            >
              Config
            </button>
            {headerMenuOpen && (
              <div
                style={{
                  position: 'absolute', top: 32, right: 0, minWidth: 180,
                  background: COLORS.bgPanel, border: `1px solid ${COLORS.border}`,
                  borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                  padding: 4, zIndex: 5,
                }}
              >
                <button
                  onClick={() => { setHeaderMenuOpen(false); handleDownloadZip(); }}
                  disabled={downloadBusy}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent',
                    color: downloadBusy ? COLORS.textDim : COLORS.text,
                    border: 'none', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, cursor: downloadBusy ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={e => { if (!downloadBusy) e.currentTarget.style.background = COLORS.bgHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {downloadBusy ? 'Downloading…' : 'Download ZIP'}
                </button>
                <div style={{ height: 1, background: COLORS.border, margin: '4px 0' }} />
                <button
                  onClick={() => { setHeaderMenuOpen(false); setDeleteError(null); setDeleteOpen(true); }}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent', color: COLORS.danger,
                    border: 'none', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = COLORS.dangerBg; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Delete widget…
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div style={{ flexShrink: 0, padding: 10, background: COLORS.dangerBg, color: COLORS.danger, fontSize: 12, borderBottom: `1px solid ${COLORS.dangerBorder}` }}>{error}</div>
      )}

      {/* Layout: LEFT (280px) FILES + VERSIONS; the right side is EITHER the
          phone preview (default) OR the read-only content window — clicking a
          file swaps the whole right side to the content window (Render/Raw)
          and its bar's "✕" swaps the preview back. */}
      <div style={{
        flex: 1, minHeight: 0, display: 'grid',
        gridTemplateColumns: editorOpen ? '280px 1fr 0px' : '280px 0px 1fr',
        transition: 'grid-template-columns 0.18s ease',
        overflow: 'hidden',
      }}>
        {/* ── Left: FILES + VERSIONS ── */}
        <div ref={leftColRef} style={{
          minHeight: 0,
          background: COLORS.bgPanel, borderRight: `1px solid ${COLORS.border}`,
          display: 'flex', flexDirection: 'column', padding: 6, gap: 12, overflowY: 'auto',
        }}>
          <FilePane
            paths={displayPaths}
            selected={selectedPath}
            onSelect={(path) => { setSelectedPath(path); setEditorOpen(true); }}
          />
          <VersionPane
            tags={tags}
            versionList={versionList}
            versionsError={versionsError}
            selectedVersion={selectedVersion}
            onSelectVersion={handleSelectVersion}
          />
        </div>

        {/* ── Center: source column (auto-folded) ── */}
        <div ref={centerColRef} style={{ minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', background: COLORS.bg, overflow: 'hidden', borderRight: `1px solid ${COLORS.border}` }}>
          <div style={{
            flexShrink: 0,
            // Unified bar height: this file header, the version-label strip,
            // and the Widget/Cover row all share `minHeight: 38px` +
            // `padding: '0 12px'` so the toolbars line up across the panes.
            minHeight: 38, boxSizing: 'border-box',
            padding: '0 12px', fontSize: 11, color: COLORS.textDim,
            borderBottom: `1px solid ${COLORS.border}`, background: COLORS.bgPanel,
            display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
          }}>
            <span style={{ fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selectedPath || 'No file selected'}
            </span>
            {/* Render/Raw switch — Render shows the file as it looks
                (markdown, html, svg, images, pretty JSON); Raw is the
                read-only source text. Images are render-only. */}
            <div style={{ display: 'inline-flex', gap: 2, background: COLORS.border, borderRadius: 7, padding: 2 }}>
              {[
                { key: 'render', label: 'Render', enabled: isRenderable },
                { key: 'raw', label: 'Raw', enabled: !isImageFile },
              ].map(({ key, label, enabled }) => {
                const on = editorView === key;
                return (
                  <button
                    key={key}
                    onClick={() => enabled && setEditorView(key)}
                    disabled={!enabled}
                    title={enabled ? '' : (key === 'render' ? 'No rendered view for this file type' : 'Binary file — no raw text view')}
                    style={{
                      background: on ? COLORS.accent : 'transparent',
                      color: on ? '#ffffff' : (enabled ? COLORS.text : COLORS.textDim),
                      border: 'none', borderRadius: 5, padding: '2px 9px',
                      fontSize: 11, fontWeight: 600,
                      cursor: enabled ? 'pointer' : 'not-allowed',
                      opacity: enabled ? 1 : 0.5,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: COLORS.textDim, fontFamily: 'ui-monospace, monospace' }}
                  title="This viewer is read-only. Edit widgets via /widget-drive.">
              read-only · edit via /widget-drive
            </span>
            {/* Close the content window → the phone preview swaps back in. */}
            <button
              onClick={() => setEditorOpen(false)}
              aria-label="Close file"
              title="Close — back to the preview"
              style={{
                background: 'transparent', color: COLORS.textDim,
                border: `1px solid ${COLORS.border}`, borderRadius: 6,
                width: 24, height: 24, lineHeight: '1', fontSize: 13,
                cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ✕
            </button>
          </div>

          {editorView === 'render' && isRenderable ? (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: COLORS.bgPanel }}>
              {isImageFile ? (
                <div style={{
                  height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'repeating-conic-gradient(#f0f0f2 0% 25%, #ffffff 0% 50%) 50% / 24px 24px',
                }}>
                  {renderImageUrl
                    ? <img src={renderImageUrl} alt={selectedPath} style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 12, color: COLORS.textDim }}>Loading image…</span>}
                </div>
              ) : (fileExt === 'html' || fileExt === 'htm') ? (
                <iframe
                  srcDoc={fileContent}
                  title={`render-${selectedPath}`}
                  sandbox="allow-scripts"
                  style={{ width: '100%', height: '100%', border: 0, background: '#ffffff', display: 'block' }}
                />
              ) : fileExt === 'svg' ? (
                <div style={{
                  height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'repeating-conic-gradient(#f0f0f2 0% 25%, #ffffff 0% 50%) 50% / 24px 24px',
                }}>
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(fileContent)}`}
                    alt={selectedPath}
                    style={{ maxWidth: '70%', maxHeight: '70%', objectFit: 'contain' }}
                  />
                </div>
              ) : fileExt === 'json' ? (
                <pre style={{
                  margin: 0, padding: 14, fontSize: 12, lineHeight: 1.5,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {(() => {
                    try { return JSON.stringify(JSON.parse(fileContent || '{}'), null, 2); }
                    catch { return fileContent; }
                  })()}
                </pre>
              ) : (
                <div style={{ padding: '8px 18px', fontSize: 13, lineHeight: 1.6, color: COLORS.text }}>
                  <MarkdownRenderer>{fileContent}</MarkdownRenderer>
                </div>
              )}
            </div>
          ) : (
            <textarea
              value={fileContent}
              readOnly
              placeholder={selectedPath ? '' : 'Click a file in the left pane.'}
              style={{
                flex: 1, minHeight: 0, background: COLORS.bg, color: COLORS.text,
                border: 'none', outline: 'none', resize: 'none', padding: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12, lineHeight: 1.5,
              }}
              spellCheck={false}
            />
          )}
        </div>

        {/* ── Right: phone preview of the SELECTED version ── */}
        <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', background: COLORS.bgPanel, borderLeft: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
          <div style={{
            flexShrink: 0,
            // Matches the file header + Widget/Cover row geometry.
            minHeight: 38, boxSizing: 'border-box',
            padding: '0 12px', borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {/* Plain display of the version being previewed. The PUBLIC badge
                shows only when that version is the release pin. Pick a
                different version in the left VERSIONS list. */}
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: selectedVersion ? COLORS.text : COLORS.textDim,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={selectedVersion ? `Previewing version ${selectedVersion}` : 'No version selected'}>
              {selectedVersion || 'no version'}
            </span>
            {isReleaseSelected && <VersionBadge kind="PUBLIC" />}
            <div style={{ flex: 1 }} />
            {/* Data source — User Data runs the real app runtime (your own
                instance feed, capture, session jumps, delete…); Mock Data
                injects example_data.json with mutations gated. Widget face
                only — Cover/Landing previews always use example data. */}
            <div style={{ display: 'inline-flex', gap: 2, background: COLORS.border, borderRadius: 7, padding: 2 }}
                 title={previewMode === 'widget'
                   ? 'User Data: use the widget for real, like in the app. Example Data: example_data.json, no real changes.'
                   : 'Data source applies to the Widget face.'}>
              {[
                { key: 'user', label: 'User Data' },
                { key: 'mock', label: 'Example Data' },
              ].map(({ key, label }) => {
                const on = dataMode === key;
                const enabled = previewMode === 'widget';
                return (
                  <button
                    key={key}
                    onClick={() => enabled && setDataMode(key)}
                    disabled={!enabled}
                    style={{
                      background: on ? COLORS.accent : 'transparent',
                      color: on ? '#ffffff' : (enabled ? COLORS.text : COLORS.textDim),
                      border: 'none', borderRadius: 5, padding: '3px 9px',
                      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                      cursor: enabled ? 'pointer' : 'not-allowed',
                      opacity: enabled ? 1 : 0.5,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {/* Appearance toggle — injects __USER_DATA__.appearance (preview
                theme). Sits at the right end of the tag strip. */}
            <div style={{ display: 'inline-flex', gap: 2, background: COLORS.border, borderRadius: 7, padding: 2 }}
                 title="Preview theme (injected as __USER_DATA__.appearance)">
              {['light', 'dark', 'auto'].map((m) => {
                const on = previewAppearance === m;
                return (
                  <button
                    key={m}
                    onClick={() => setPreviewAppearance(m)}
                    style={{
                      background: on ? COLORS.accent : 'transparent',
                      color: on ? '#ffffff' : COLORS.text,
                      border: 'none', borderRadius: 5, padding: '3px 9px',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      textTransform: 'capitalize', whiteSpace: 'nowrap',
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
          {/* ── Second toolbar row: Widget/Cover + Data/No-Data ──
              These swap what the preview iframe shows so the designer can
              confirm cover.html and the live widget BOTH render cleanly
              with and without example user data — covering all 4 quadrants
              of (cover, widget) × (data, no data) without leaving the page
              or editing example_data.json. Lives in its own row right under
              the tag tabs + Light/Dark/Auto bar. */}
          <div style={{
            flexShrink: 0,
            // Matches the file header + version-label strip geometry.
            minHeight: 38, boxSizing: 'border-box',
            padding: '0 12px', borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', gap: 8,
            background: COLORS.bgPanel,
          }}>
            {/* Preview face — Widget · Cover · Icon · Landing (pill buttons).
                Swaps what the phone preview shows: the live widget (index.html),
                the catalog tile (cover preset/cover.html), the widget mark (icon),
                or the intro page (landing). */}
            <div style={{ display: 'inline-flex', gap: 2, background: COLORS.border, borderRadius: 7, padding: 2 }}
                 title="Choose which face of the widget to preview.">
              {[
                { key: 'widget', label: 'Widget' },
                { key: 'cover', label: 'Cover' },
                { key: 'icon', label: 'Icon' },
                { key: 'landing', label: 'Landing' },
              ].map(({ key, label }) => {
                const on = previewMode === key;
                return (
                  <button
                    key={key}
                    onClick={() => setPreviewMode(key)}
                    style={{
                      background: on ? COLORS.accent : 'transparent',
                      color: on ? '#ffffff' : COLORS.text,
                      border: 'none', borderRadius: 5, padding: '3px 10px',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {/* Cover preset selector — only while previewing Cover. Preview-only:
                compare all 5 host presets against the version's example data.
                The committed choice lives in the manifest (edit via /widget-drive). */}
            {previewCover && (
              <select
                value={previewCoverPreset}
                onChange={(e) => setPreviewCoverPreset(e.target.value)}
                title="Cover preset — preview only. Edit the committed choice via /widget-drive."
                style={{
                  background: COLORS.bgPanel, color: COLORS.text,
                  border: `1px solid ${COLORS.border}`, borderRadius: 7,
                  padding: '4px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  appearance: 'auto',
                }}
              >
                {COVER_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            )}
            {/* Landing type selector — only while previewing Landing. Preview-only:
                Default page = the host-rendered landing.json; Custom HTML = the
                widget's own landing.html. Edit the committed choice via /widget-drive. */}
            {previewLanding && (
              <select
                value={previewLandingType}
                onChange={(e) => setPreviewLandingType(e.target.value)}
                title="Landing type — preview only. Edit the committed choice via /widget-drive."
                style={{
                  background: COLORS.bgPanel, color: COLORS.text,
                  border: `1px solid ${COLORS.border}`, borderRadius: 7,
                  padding: '4px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  appearance: 'auto',
                }}
              >
                <option value="json">Default page</option>
                <option value="html">Custom HTML</option>
              </select>
            )}
            <div style={{ flex: 1 }} />
          </div>
          {/* Widget face → WidgetRuntime (the absorbed Widget Use runtime:
              User Data = real instance feed + full app bridge; Mock Data =
              example_data.json, mutations gated). Cover/Icon/Landing keep
              the dedicated blob pipeline (tile size, icon mark, landing). */}
          {previewMode === 'widget' ? (
            <PhonePreview colors={COLORS}>
              <WidgetRuntime
                key={`runtime-${runtimeNonce}`}
                widgetId={widgetId}
                widget={widget}
                dataMode={dataMode}
                requestedInstanceId={requestedInstanceId}
                previewTag={isReleaseSelected ? 'release' : 'dev'}
                previewVersion={selectedVersion}
                pinnedSessionId={null}
                previewSha={previewSha}
                appearance={previewAppearance}
                onOpenMainSession={setMainSession}
              />
              {/* "Chat with widget" → the widget's main session view shown IN
                  PLACE inside the iPhone preview face (cover + chat + input),
                  like the app screen — NOT a full-window takeover, NOT a nav. */}
              {mainSession && (
                <WidgetMainSessionPanel
                  sessionId={mainSession.sessionId}
                  widgetId={mainSession.widgetId || widgetId}
                  instanceId={mainSession.instanceId}
                  tag={mainSession.tag || (isReleaseSelected ? 'release' : 'dev')}
                  ask={mainSession.ask}
                  onClose={() => setMainSession(null)}
                />
              )}
            </PhonePreview>
          ) : (
            <PhonePreview
              previewUrl={previewUrl}
              previewRef={previewRef}
              colors={COLORS}
              tileSize={previewCover ? { w: 240, h: 170 } : null}
              iconUrl={previewIcon ? iconPreviewUrl : null}
              iconOnly={previewIcon}
            />
          )}
        </div>
      </div>

      {schemaOpen && (
        <SchemaPopup
          widgetId={widgetId}
          version={selectedVersion}
          onClose={() => setSchemaOpen(false)}
          onUpgraded={reloadWidget}
        />
      )}

      {deleteOpen && (
        <DeleteWidgetModal
          widget={widget}
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => { if (!deleteBusy) { setDeleteOpen(false); setDeleteError(null); } }}
          onConfirm={handleDeleteConfirm}
        />
      )}

    </div>
  );
}
